import { useState, useEffect } from 'react';

// Import all landing page components
import Header from './landing/Header';
import HeroSection from './landing/HeroSection';
import LogosSection from './landing/LogosSection';
import TrustSection from './landing/TrustSection';
import CTABanner from './landing/CTABanner';
import ServicesSection from './landing/ServicesSection';
import ProcessSection from './landing/ProcessSection';
import ResultsSection from './landing/ResultsSection';
import { QuizPreviewSection, QuizOverlay, useQuiz } from './landing/QuizSection';
import FAQSection from './landing/FAQSection';
import ContactSection from './landing/ContactSection';
import Footer from './landing/Footer';
import FloatingElements from './landing/FloatingElements';

const LandingPage = () => {
  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Active section state for nav highlighting
  const [activeSection, setActiveSection] = useState('');

  // Navigation links - matching the section order
  const navLinks = [
    { id: 'trust', label: 'Pourquoi nous' },
    { id: 'services', label: 'Services' },
    { id: 'parcours', label: 'Parcours' },
    { id: 'temoignages', label: 'Témoignages' },
    { id: 'faq', label: 'FAQ' },
    { id: 'contact', label: 'Contact' }
  ];

  // Quiz hook
  const quiz = useQuiz();

  // Intersection Observer for active section highlighting
  useEffect(() => {
    const sectionIds = ['trust', 'services', 'parcours', 'resultats', 'test-niveau', 'temoignages', 'faq', 'contact'];
    const sections = sectionIds.map(id => document.getElementById(id)).filter(Boolean);
    
    const observerOptions = {
      root: null,
      rootMargin: '-10% 0px -70% 0px',
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, observerOptions);

    sections.forEach(section => observer.observe(section));

    return () => sections.forEach(section => observer.unobserve(section));
  }, []);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isMobileMenuOpen]);

  // Handle nav link click
  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* 1. Navbar */}
      <Header 
        navLinks={navLinks}
        activeSection={activeSection}
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        handleNavClick={handleNavClick}
      />

      {/* 2. Hero Section (Hero + social proof + CTA) */}
      <HeroSection onStartQuiz={quiz.startQuiz} />

      {/* 3. Logos Section (partenaires + Goethe + ambassades) */}
      <LogosSection />

      {/* 4. Trust Section (Problem/Agitation — crédibilité) */}
      <TrustSection />

      {/* CTA Banner */}
      <CTABanner />

      {/* 5. Services Section (Solution — what Speak&Move offers) */}
      <ServicesSection />

      {/* 6. Process Section (Bento Grid — Features/Benefits) */}
      <ProcessSection />

      {/* 8. Quiz Section (Engagement — test de niveau) */}
      <QuizPreviewSection onStartQuiz={quiz.startQuiz} />

      {/* 9. Results + Testimonials (merged) */}
      <ResultsSection />

      {/* 10. FAQ Section (Objection Handling — juste avant le CTA) */}
      <FAQSection />

      {/* 11. Contact Section (Final CTA — formulaire) */}
      <ContactSection />

      {/* 12. Footer */}
      <Footer />

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .animate-scroll {
          animation: scroll 35s linear infinite;
          transition: animation-duration 0.5s ease;
        }
        .testimonials-track:hover {
          animation-duration: 120s;
        }
        .testimonials-track {
          width: max-content;
        }
        
        @keyframes scroll-slow {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .animate-scroll-slow {
          animation: scroll-slow 50s linear infinite;
          transition: animation-duration 0.5s ease;
        }
        .logos-track {
          width: max-content;
        }
        .logos-track:hover {
          animation-duration: 150s;
        }
        .partners-track {
          width: max-content;
        }
        .partners-track:hover {
          animation-duration: 150s;
        }

        /* Cylinder animations */
        .stat-cylinder {
          width: 70px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .cylinder-body {
          width: 70px;
          border-radius: 35px 35px 0 0;
          animation: grow-cylinder 1.5s ease-out forwards;
        }
        .cylinder-bottom {
          width: 70px;
          height: 20px;
          margin-top: -10px;
          transform: scaleY(0.3);
        }
        
        .cylinder-1 .cylinder-body {
          height: 0;
          animation: grow-cylinder-1 1.5s ease-out forwards;
          animation-delay: 0.2s;
        }
        .cylinder-2 .cylinder-body {
          height: 0;
          animation: grow-cylinder-2 1.5s ease-out forwards;
          animation-delay: 0.4s;
        }
        .cylinder-3 .cylinder-body {
          height: 0;
          animation: grow-cylinder-3 1.5s ease-out forwards;
          animation-delay: 0.6s;
        }
        .cylinder-4 .cylinder-body {
          height: 0;
          animation: grow-cylinder-4 1.5s ease-out forwards;
          animation-delay: 0.8s;
        }

        @keyframes grow-cylinder-1 {
          0% { height: 0; }
          100% { height: 80px; }
        }
        @keyframes grow-cylinder-2 {
          0% { height: 0; }
          100% { height: 130px; }
        }
        @keyframes grow-cylinder-3 {
          0% { height: 0; }
          100% { height: 180px; }
        }
        @keyframes grow-cylinder-4 {
          0% { height: 0; }
          100% { height: 230px; }
        }

        @media (min-width: 768px) {
          .stat-cylinder {
            width: 90px;
          }
          .cylinder-body {
            width: 90px;
            border-radius: 45px 45px 0 0;
          }
          .cylinder-bottom {
            width: 90px;
            height: 25px;
          }
          @keyframes grow-cylinder-1 {
            0% { height: 0; }
            100% { height: 100px; }
          }
          @keyframes grow-cylinder-2 {
            0% { height: 0; }
            100% { height: 160px; }
          }
          @keyframes grow-cylinder-3 {
            0% { height: 0; }
            100% { height: 210px; }
          }
          @keyframes grow-cylinder-4 {
            0% { height: 0; }
            100% { height: 260px; }
          }
        }

        @keyframes scaleIn {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>

      {/* Quiz Overlay */}
      <QuizOverlay 
        showQuiz={quiz.showQuiz}
        quizStep={quiz.quizStep}
        answers={quiz.answers}
        quizContact={quiz.quizContact}
        quizResult={quiz.quizResult}
        isQuizSubmitting={quiz.isQuizSubmitting}
        onClose={quiz.closeQuiz}
        onBegin={quiz.beginQuizQuestions}
        onAnswer={quiz.handleQuizAnswer}
        onNext={quiz.handleQuizNext}
        onPrev={quiz.handleQuizPrev}
        onContactChange={quiz.setQuizContact}
        onSubmitContact={quiz.submitQuizContact}
      />

      {/* Floating Elements (WhatsApp button, Mobile CTA) */}
      <FloatingElements />
    </div>
  );
};

export default LandingPage;
